import { ref } from 'vue';
import { MOBILE_MAX_PX } from '@/constants/layout-breakpoints';

const DEFAULT_DURATION_MS = 1500;
/** H5 Snackbar 默认展示时长（海外短确认） */
const H5_SNACKBAR_DURATION_MS = 2000;
const THROTTLE_MS = 1000;
const PLACEMENT_GAP = 6;
const VIEWPORT_PAD = 8;
/** PC 锚点提示块最大宽度：可多行，避免过窄挤字 */
const HINT_MAX_WIDTH_CAP = 360;

/** @typedef {'top' | 'right' | 'left' | 'bottom'} HintPlacement */

/** @type {HintPlacement[]} */
const PLACEMENT_ORDER = ['top', 'right', 'left', 'bottom'];

/** @type {import('vue').Ref<{ id: number, text: string, top: number, left: number, width: number, maxWidth: number, color: string, fontSize: string, fontWeight: string } | null>} */
export const activeHint = ref(null);

/** H5 贴底 Snackbar（与 activeHint 互斥） */
/** @type {import('vue').Ref<{ id: number, text: string } | null>} */
export const activeSnackbar = ref(null);

let hintSeq = 0;
let hideTimer = null;
let rafId = null;
/** @type {WeakMap<HTMLElement, number>} */
const lastShownAtByAnchor = new WeakMap();
/** @type {{ anchor: HTMLElement, text: string, typography: { fontSize: string, fontWeight: string }, placement: HintPlacement } | null} */
let activeSession = null;

function isH5Viewport() {
  return typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX_PX;
}

function resolveAnchorEl(anchor) {
  if (!anchor) return null;
  if (anchor instanceof HTMLElement) return anchor;
  if (
    anchor &&
    typeof anchor === 'object' &&
    '$el' in anchor &&
    anchor.$el instanceof HTMLElement
  ) {
    return anchor.$el;
  }
  if (anchor && typeof anchor === 'object' && 'value' in anchor) {
    return resolveAnchorEl(anchor.value);
  }
  return null;
}

/** 解析操作按钮锚点：优先外层 button，且必须是仍在文档内的节点 */
export function resolveHintAnchor(anchor) {
  const el = resolveAnchorEl(anchor);
  if (!(el instanceof HTMLElement)) return null;
  if (!document.contains(el)) return null;

  const interactive =
    el.closest(
      'button, [role="button"], .referrals-page__copy-btn, .h5-referrals__copy-btn, .deposit-dlg__copy-btn, .h5-icon-button'
    ) ?? el;

  if (!(interactive instanceof HTMLElement) || !document.contains(interactive)) {
    return document.contains(el) ? el : null;
  }
  return interactive;
}

function parseRgb(color) {
  const raw = String(color ?? '').trim();
  if (!raw || raw === 'transparent') return null;
  if (raw.startsWith('#')) {
    const hex = raw.slice(1);
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
      };
    }
    if (hex.length >= 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
  }
  const match = raw.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (!match) return null;
  return {
    r: Number(match[1]),
    g: Number(match[2]),
    b: Number(match[3]),
  };
}

function relativeLuminance({ r, g, b }) {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function isTransparentColor(color) {
  const raw = String(color ?? '')
    .trim()
    .toLowerCase();
  if (!raw || raw === 'transparent') return true;
  const match = raw.match(/rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*([\d.]+)\s*\)/i);
  if (match) return Number(match[1]) <= 0.05;
  return false;
}

/** 与操作点配色一致：实心按钮取背景色，文本/线框按钮取文字色 */
export function resolveAnchorHintColor(el) {
  if (!(el instanceof HTMLElement) || typeof window === 'undefined') {
    return 'var(--text-color, #0f172a)';
  }

  const icon = el.querySelector('.el-icon, svg');
  const source = icon instanceof HTMLElement ? icon : el;
  const style = window.getComputedStyle(source);
  const fg = style.color;
  const bg = window.getComputedStyle(el).backgroundColor;
  const fgRgb = parseRgb(fg);
  const bgRgb = parseRgb(bg);

  if (fgRgb && bgRgb && !isTransparentColor(bg)) {
    const fgLum = relativeLuminance(fgRgb);
    const bgLum = relativeLuminance(bgRgb);
    if (fgLum > 0.82 && bgLum < 0.72) return bg;
    if (fgLum < 0.25 && bgLum > 0.88) return fg;
  }

  if (fgRgb && relativeLuminance(fgRgb) > 0.82 && bgRgb && !isTransparentColor(bg)) {
    return bg;
  }

  return fg || 'var(--text-color, #0f172a)';
}

function resolveAnchorHintTypography(el) {
  if (!(el instanceof HTMLElement) || typeof window === 'undefined') {
    return { fontSize: '12px', fontWeight: '600' };
  }
  const style = window.getComputedStyle(el);
  // 图标按钮常写 font-size:0，不能原样继承，否则提示不可见
  const rawPx = parseFloat(style.fontSize);
  const fontSizePx = Number.isFinite(rawPx) && rawPx >= 10 ? rawPx : 12;
  return {
    fontSize: `${fontSizePx}px`,
    fontWeight: style.fontWeight && style.fontWeight !== '0' ? style.fontWeight : '600',
  };
}

/** 与渲染 max-width 一致：视口内夹紧，且不超过 HINT_MAX_WIDTH_CAP */
function resolveHintMaxWidth() {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
  return Math.max(24, Math.min(HINT_MAX_WIDTH_CAP, vw - VIEWPORT_PAD * 2));
}

/**
 * 按与 DOM 相同的 max-width 估算换行后的宽高（短文案单行，过长自动多行）。
 * @param {string} text
 * @param {{ fontSize: string, fontWeight: string }} typography
 */
function measureHintSize(text, typography) {
  const fontSize = parseFloat(typography.fontSize) || 12;
  const lineHeight = fontSize * 1.3;
  const hasCjk = /[\u4e00-\u9fff\u3400-\u4dbf]/.test(text);
  // 略偏宽，避免低估行数导致「上方」实际渲染盖住按钮
  const charWidth = fontSize * (hasCjk ? 1 : 0.62);
  const maxWidth = resolveHintMaxWidth();
  const naturalWidth = Math.max(24, Math.ceil(String(text).length * charWidth + 4));
  const width = Math.min(naturalWidth, maxWidth);
  const lines = naturalWidth <= maxWidth ? 1 : Math.ceil(naturalWidth / maxWidth);
  const height = Math.ceil(lineHeight * Math.max(1, lines) + (lines > 1 ? 4 : 0));
  return { width, height, maxWidth };
}

/**
 * 上/下水平锚点：短于按钮则居中；宽于按钮时按锚点在左/右半屏对齐
 * （左侧操作靠左展开，右侧操作靠右展开，避免盖住同行 input），再夹进视口。
 */
function resolveVerticalHintLeft(anchorRect, hintSize) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
  const anchorCenterX = anchorRect.left + anchorRect.width / 2;
  const preferRightAlign = anchorCenterX > vw / 2;

  let left;
  if (hintSize.width <= anchorRect.width) {
    left = anchorRect.left + (anchorRect.width - hintSize.width) / 2;
  } else if (preferRightAlign) {
    left = anchorRect.right - hintSize.width;
  } else {
    left = anchorRect.left;
  }

  const maxLeft = vw - VIEWPORT_PAD - hintSize.width;
  left = Math.max(VIEWPORT_PAD, Math.min(left, maxLeft));
  return left;
}

/**
 * 任意方向最终 box 都夹在视口内（左缘/右缘操作、上右左下一致）。
 * width 已 ≤ max-width，水平夹紧不会把 hint 拉变形。
 * @param {{ left: number, top: number, width: number, height: number }} box
 */
function clampHintBox(box) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 640;
  const maxLeft = vw - VIEWPORT_PAD - box.width;
  const maxTop = vh - VIEWPORT_PAD - box.height;

  return {
    ...box,
    left: Math.max(VIEWPORT_PAD, Math.min(box.left, maxLeft)),
    top: Math.max(VIEWPORT_PAD, Math.min(box.top, Math.max(VIEWPORT_PAD, maxTop))),
  };
}

/**
 * hint 是否压到锚点（含间距）。clamp 把「上方」压回视口时最容易误盖住按钮。
 * @param {{ left: number, top: number, width: number, height: number }} box
 * @param {DOMRect} anchorRect
 */
function overlapsAnchor(box, anchorRect) {
  const gap = PLACEMENT_GAP;
  const ax1 = anchorRect.left - gap;
  const ay1 = anchorRect.top - gap;
  const ax2 = anchorRect.right + gap;
  const ay2 = anchorRect.bottom + gap;
  return !(
    box.left + box.width <= ax1 ||
    box.left >= ax2 ||
    box.top + box.height <= ay1 ||
    box.top >= ay2
  );
}

/**
 * @param {HintPlacement} placement
 * @param {DOMRect} anchorRect
 * @param {{ width: number, height: number }} hintSize
 * @param {{ clamp?: boolean }} [options]
 */
function getHintBox(placement, anchorRect, hintSize, options = {}) {
  const gap = PLACEMENT_GAP;
  const cy = anchorRect.top + anchorRect.height / 2;
  const shouldClamp = options.clamp !== false;

  /** @type {{ left: number, top: number, width: number, height: number }} */
  let box;
  switch (placement) {
    case 'right':
      box = {
        left: anchorRect.right + gap,
        top: cy - hintSize.height / 2,
        width: hintSize.width,
        height: hintSize.height,
      };
      break;
    case 'left':
      box = {
        left: anchorRect.left - gap - hintSize.width,
        top: cy - hintSize.height / 2,
        width: hintSize.width,
        height: hintSize.height,
      };
      break;
    case 'bottom':
      box = {
        left: resolveVerticalHintLeft(anchorRect, hintSize),
        top: anchorRect.bottom + gap,
        width: hintSize.width,
        height: hintSize.height,
      };
      break;
    case 'top':
    default:
      box = {
        left: resolveVerticalHintLeft(anchorRect, hintSize),
        top: anchorRect.top - gap - hintSize.height,
        width: hintSize.width,
        height: hintSize.height,
      };
      break;
  }
  return shouldClamp ? clampHintBox(box) : box;
}

function isOutOfViewport(box) {
  const vw = window.innerWidth || 0;
  const vh = window.innerHeight || 0;
  const eps = 0.5;
  return (
    box.left < VIEWPORT_PAD - eps ||
    box.top < VIEWPORT_PAD - eps ||
    box.left + box.width > vw - VIEWPORT_PAD + eps ||
    box.top + box.height > vh - VIEWPORT_PAD + eps
  );
}

function hasDirectText(el) {
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && String(node.textContent ?? '').trim()) {
      return true;
    }
  }
  return false;
}

/** 是否属于按钮所在输入行（左侧兄弟 input 文本，不应挡「上方」） */
function isInputRowSibling(anchorEl, el) {
  const row = anchorEl.closest(
    '.referrals-page__input-row, .h5-referrals__readonly-row, .deposit-dlg__copy-row, [data-anchor-hint-row]'
  );
  return Boolean(row && row.contains(el) && el !== anchorEl && !anchorEl.contains(el));
}

/**
 * 判断元素是否为 hint 需要避让的内容（相对当前操作按钮）。
 * @param {Element} el
 * @param {HTMLElement} anchorEl
 * @param {HintPlacement} placement
 */
function isObstacle(el, anchorEl, placement) {
  if (!(el instanceof Element)) return false;
  if (el === document.body || el === document.documentElement || el.id === 'app') return false;
  if (el.closest('.ds-anchor-hint')) return false;
  if (el === anchorEl || anchorEl.contains(el) || el.contains(anchorEl)) return false;

  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || style.pointerEvents === 'none') {
    return false;
  }
  const opacity = parseFloat(style.opacity);
  if (!Number.isNaN(opacity) && opacity < 0.08) return false;

  // 表单 label 不算遮挡
  if (el.matches('label')) return false;

  // 上下方向：同一输入行里的 input/文本在按钮左侧，不应挡「上方/下方」
  if ((placement === 'top' || placement === 'bottom') && isInputRowSibling(anchorEl, el)) {
    return false;
  }

  if (
    el.matches(
      'img, video, canvas, picture, input, textarea, select, button, a, svg, [role="img"], .el-input__inner, .el-textarea__inner'
    )
  ) {
    return true;
  }

  if (el.matches('h1, h2, h3, h4, h5, h6, p, li, td, th')) {
    return Boolean(el.textContent?.trim());
  }

  if (el.matches('span')) {
    return Boolean(el.textContent?.trim());
  }

  return hasDirectText(el);
}

/** @param {{ left: number, top: number, width: number, height: number }} box @param {HTMLElement} anchorEl @param {HintPlacement} placement */
function isHintBoxBlocked(box, anchorEl, placement) {
  const sampleRatios = [
    [0.5, 0.5],
    [0.15, 0.2],
    [0.85, 0.2],
    [0.15, 0.8],
    [0.85, 0.8],
  ];

  for (const [rx, ry] of sampleRatios) {
    const x = box.left + box.width * rx;
    const y = box.top + box.height * ry;
    if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) continue;

    const stack = document.elementsFromPoint(x, y);
    for (const el of stack) {
      if (!(el instanceof Element)) continue;
      if (el === document.body || el === document.documentElement) break;
      if (el.id === 'app') continue;
      if (isObstacle(el, anchorEl, placement)) return true;
      if (isTransparentColor(window.getComputedStyle(el).backgroundColor)) continue;
      break;
    }
  }

  return false;
}

/**
 * 生成候选：先按理想位置；若 clamp 后压到锚点则丢弃该方向。
 * @param {HintPlacement} placement
 * @param {DOMRect} anchorRect
 * @param {{ width: number, height: number, maxWidth: number }} hintSize
 */
function tryPlacementBox(placement, anchorRect, hintSize) {
  const raw = getHintBox(placement, anchorRect, hintSize, { clamp: false });
  if (isOutOfViewport(raw)) return null;
  const box = clampHintBox(raw);
  // clamp 不得把 hint 拽回盖住按钮（分享等右上角多行提示常见）
  if (overlapsAnchor(box, anchorRect)) return null;
  if (isOutOfViewport(box)) return null;
  return box;
}

/**
 * 按 上 → 右 → 左 → 下 选取首个可用方向；坐标即 hint 左上角（与渲染一致）。
 * @param {HTMLElement} el
 * @param {string} text
 * @param {{ fontSize: string, fontWeight: string }} typography
 * @param {HintPlacement} [lockedPlacement]
 */
function pickHintBox(el, text, typography, lockedPlacement) {
  const anchorRect = el.getBoundingClientRect();
  const hintSize = measureHintSize(text, typography);

  if (lockedPlacement) {
    const locked =
      tryPlacementBox(lockedPlacement, anchorRect, hintSize) ||
      getHintBox(lockedPlacement, anchorRect, hintSize);
    return {
      box: locked,
      placement: lockedPlacement,
      maxWidth: hintSize.maxWidth,
    };
  }

  for (const placement of PLACEMENT_ORDER) {
    const box = tryPlacementBox(placement, anchorRect, hintSize);
    if (!box) continue;
    if (isHintBoxBlocked(box, el, placement)) continue;
    return { box, placement, maxWidth: hintSize.maxWidth };
  }

  // 文案区挡路时仍优先不压锚点：下 → 左 → 上 → 右
  for (const placement of ['bottom', 'left', 'top', 'right']) {
    const box = tryPlacementBox(placement, anchorRect, hintSize);
    if (!box) continue;
    return { box, placement, maxWidth: hintSize.maxWidth };
  }

  // 最后兜底：下方右对齐（分享/卖出在右缘时最稳），再夹视口
  return {
    box: getHintBox('bottom', anchorRect, hintSize),
    placement: 'bottom',
    maxWidth: hintSize.maxWidth,
  };
}

function updateActiveHintPosition() {
  if (!activeSession || !activeHint.value) return;

  const { anchor, text, typography, placement } = activeSession;
  if (!document.contains(anchor)) {
    hideActiveHint();
    return;
  }

  const { box, maxWidth } = pickHintBox(anchor, text, typography, placement);
  activeHint.value = {
    ...activeHint.value,
    top: box.top,
    left: box.left,
    width: box.width,
    maxWidth,
  };
}

function schedulePositionUpdate() {
  if (typeof window === 'undefined' || !activeSession) return;
  if (rafId != null) return;
  rafId = window.requestAnimationFrame(() => {
    rafId = null;
    updateActiveHintPosition();
  });
}

function attachScrollListeners() {
  if (typeof window === 'undefined') return;
  window.addEventListener('scroll', schedulePositionUpdate, true);
  window.addEventListener('resize', schedulePositionUpdate);
}

function detachScrollListeners() {
  if (typeof window === 'undefined') return;
  window.removeEventListener('scroll', schedulePositionUpdate, true);
  window.removeEventListener('resize', schedulePositionUpdate);
  if (rafId != null) {
    window.cancelAnimationFrame(rafId);
    rafId = null;
  }
}

function clearHintTimer() {
  if (hideTimer != null) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function hideActiveHint() {
  clearHintTimer();
  detachScrollListeners();
  activeSession = null;
  activeHint.value = null;
  activeSnackbar.value = null;
}

/**
 * @param {object} options
 * @param {HTMLElement | import('vue').Ref | Event['currentTarget']} options.anchor
 * @param {string} options.text
 * @param {number} [options.duration]
 * @param {boolean} [options.skipDedupe]
 */
export function showAnchorHint({
  anchor,
  text,
  duration = DEFAULT_DURATION_MS,
  skipDedupe = false,
}) {
  const msg = String(text ?? '').trim();
  if (!msg) return;

  const el = resolveHintAnchor(anchor);
  if (!el) return;

  const now = Date.now();
  if (!skipDedupe) {
    const last = lastShownAtByAnchor.get(el) ?? 0;
    if (now - last < THROTTLE_MS) return;
    lastShownAtByAnchor.set(el, now);
  }

  detachScrollListeners();
  clearHintTimer();
  activeSession = null;
  activeHint.value = null;
  activeSnackbar.value = null;

  // H5：海外 Material 贴底 Snackbar；PC：锚点浮字
  if (isH5Viewport()) {
    activeSnackbar.value = {
      id: ++hintSeq,
      text: msg,
    };
    const hideMs = duration === DEFAULT_DURATION_MS ? H5_SNACKBAR_DURATION_MS : duration;
    hideTimer = setTimeout(() => {
      hideTimer = null;
      hideActiveHint();
    }, Math.max(hideMs, 400));
    return;
  }

  const typography = resolveAnchorHintTypography(el);
  const { box, placement, maxWidth } = pickHintBox(el, msg, typography);

  activeSession = { anchor: el, text: msg, typography, placement };
  attachScrollListeners();

  activeHint.value = {
    id: ++hintSeq,
    text: msg,
    top: box.top,
    left: box.left,
    width: box.width,
    maxWidth,
    color: resolveAnchorHintColor(el),
    fontSize: typography.fontSize,
    fontWeight: typography.fontWeight,
  };

  hideTimer = setTimeout(() => {
    hideTimer = null;
    hideActiveHint();
  }, Math.max(duration, 400));
}

export function useAnchorHintState() {
  return { activeHint, activeSnackbar };
}

export function useAnchorHint() {
  return { show: showAnchorHint };
}

/** @deprecated 保留导出避免旧引用报错；现用左上角直定位，不再依赖 transform */
export function anchorHintTransform() {
  return 'none';
}
